import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// 🔐 Типы для Granular Permissions
export type SystemRole = 
  | 'super_admin' 
  | 'platform_owner' 
  | 'regional_admin' 
  | 'hotel_group_admin'
  | 'hotel_owner'
  | 'hotel_manager'
  | 'receptionist'
  | 'housekeeping'
  | 'support';

export interface UserPermissions {
  [section: string]: string[]; // e.g., { bookings: ['view', 'create'] }
}

// 🚀 Утилита для очистки токенов (без логов в продакшене)
const cleanupAuthState = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  if (typeof sessionStorage !== 'undefined') {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
  }
};

interface AuthDiagnostics {
  sessionExists: boolean;
  userExists: boolean;
  tokenValid: boolean;
  lastCheck: Date | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  permissions: UserPermissions | null;
  permissionsLoading: boolean;
  systemRole: SystemRole | null;
  hasPermission: (section: string, action?: string) => boolean;
  refreshPermissions: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  forceReset: () => Promise<void>;
  diagnostics: AuthDiagnostics;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: false,
  permissions: null,
  permissionsLoading: false,
  systemRole: null,
  hasPermission: () => false,
  refreshPermissions: async () => {},
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  forceReset: async () => {},
  diagnostics: {
    sessionExists: false,
    userExists: false,
    tokenValid: false,
    lastCheck: null,
  },
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [systemRole, setSystemRole] = useState<SystemRole | null>(null);
  
  const [diagnostics, setDiagnostics] = useState<AuthDiagnostics>({
    sessionExists: false,
    userExists: false,
    tokenValid: false,
    lastCheck: null,
  });
  const { toast } = useToast();

  // 🔐 Загрузка permissions из БД
  const loadPermissions = useCallback(async (userId: string) => {
    setPermissionsLoading(true);
    try {
      const { data: systemUser } = await supabase
        .from('system_users')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (systemUser) {
        const role = systemUser.role as SystemRole;
        setSystemRole(role);
        
        if (['super_admin', 'platform_owner'].includes(role)) {
          setPermissions({ '*': ['*'] });
        } else {
          // Функция get_user_permissions сама получит hotel_id из user_permissions
          const { data: permsData, error: permsError } = await supabase
            .rpc('get_user_permissions', { p_user_id: userId });
          
          if (permsError) {
            console.error('Error loading permissions:', permsError);
            setPermissions({});
          } else {
            setPermissions((permsData || {}) as UserPermissions);
          }
        }
      } else {
        setSystemRole(null);
        setPermissions({});
      }
    } catch (error) {
      console.error('Error in loadPermissions:', error);
      setPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  // 🔐 Проверка конкретного permission
  const hasPermission = useCallback((section: string, action: string = 'view'): boolean => {
    if (systemRole && ['super_admin', 'platform_owner'].includes(systemRole)) {
      return true;
    }
    
    if (!permissions) return false;
    if (permissions['*']?.includes('*')) return true;
    
    const sectionPerms = permissions[section];
    if (!sectionPerms) return false;
    
    return sectionPerms.includes(action) || sectionPerms.includes('*');
  }, [permissions, systemRole]);

  const refreshPermissions = useCallback(async () => {
    if (user?.id) {
      await loadPermissions(user.id);
    }
  }, [user?.id, loadPermissions]);

  const checkTokenStatus = async (): Promise<boolean> => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      return !error && !!user;
    } catch {
      return false;
    }
  };

  const updateDiagnostics = async (currentSession: Session | null, currentUser: User | null) => {
    const tokenValid = await checkTokenStatus();
    setDiagnostics({
      sessionExists: !!currentSession,
      userExists: !!currentUser,
      tokenValid,
      lastCheck: new Date(),
    });
  };

  const forceReset = async () => {
    try {
      cleanupAuthState();
      await supabase.auth.signOut({ scope: 'global' });
      setUser(null);
      setSession(null);
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  useEffect(() => {
    let isMounted = true;
    let initialSessionFired = false;
    
    // 🛡️ Фоновая проверка is_approved (fire-and-forget)
    const checkApproval = async (userId: string) => {
      try {
        const { data: approval } = await supabase
          .from('user_approvals')
          .select('is_approved')
          .eq('user_id', userId)
          .maybeSingle();

        if (approval && approval.is_approved === false) {
          console.warn('[AuthContext] Пользователь заблокирован, принудительный выход');
          await supabase.auth.signOut();
          if (isMounted) {
            setUser(null);
            setSession(null);
            setPermissions(null);
            setSystemRole(null);
            setLoading(false);
          }
        }
      } catch (err) {
        console.warn('[AuthContext] approval check failed, allowing session:', (err as Error)?.message);
      }
    };

    // 1. Регистрируем listener — СИНХРОННЫЙ callback, без setTimeout
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') initialSessionFired = true;
      if (!isMounted) return;

      // Аномалия: SIGNED_IN без session -> forceReset
      if (event === 'SIGNED_IN' && !session) {
        setTimeout(() => { if (isMounted) forceReset(); }, 1000);
        return;
      }

      // ⚡ АК-47: мгновенное обновление state — БЕЗ await, БЕЗ setTimeout
      if (session?.user) {
        setUser(session.user);
        setSession(session);
        setLoading(false);

        // Фоновая проверка approval (только для INITIAL_SESSION и TOKEN_REFRESHED)
        // SIGNED_IN не проверяем — signIn() уже проверил
        if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
          void checkApproval(session.user.id);
        }

        setTimeout(() => {
          if (isMounted) updateDiagnostics(session, session.user);
        }, 100);
      } else {
        // SIGNED_OUT или null-сессия — явная очистка ВСЕГО
        setUser(null);
        setSession(null);
        setPermissions(null);
        setSystemRole(null);
        setLoading(false);
      }
    });

    // 2. Страховка: если INITIAL_SESSION не стрельнул за 2 сек — дёргаем getSession вручную
    const fallbackTimer = setTimeout(async () => {
      if (!isMounted || initialSessionFired) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (isMounted && !initialSessionFired) {
          if (session?.user) {
            setUser(session.user);
            setSession(session);
            setLoading(false);
            void checkApproval(session.user.id);
          } else {
            setUser(null);
            setSession(null);
            setLoading(false);
          }
        }
      } catch {
        if (isMounted) setLoading(false);
      }
    }, 2000);

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      const { user: signedInUser, session: signedInSession } = data;
      
      if (signedInUser) {
        // ⚡ Параллельная проверка approval + роли
        const [approvalResult, systemUserResult] = await Promise.all([
          supabase
            .from('user_approvals')
            .select('is_approved')
            .eq('user_id', signedInUser.id)
            .maybeSingle(),
          supabase
            .from('system_users')
            .select('role')
            .eq('user_id', signedInUser.id)
            .maybeSingle()
        ]);

        if (!approvalResult.data?.is_approved) {
          await supabase.auth.signOut();
          return { 
            error: { 
              message: 'Ваш аккаунт ожидает одобрения администратора. Пожалуйста, свяжитесь с поддержкой.' 
            } 
          };
        }

        if (!systemUserResult.data || !['super_admin', 'platform_owner', 'regional_admin', 'hotel_owner', 'hotel_manager'].includes(systemUserResult.data.role)) {
          await supabase.auth.signOut();
          return { 
            error: { 
              message: 'У вас нет прав доступа к этой системе.' 
            } 
          };
        }

        // 🛡️ АК-47: Устанавливаем user/session НАПРЯМУЮ — не ждём onAuthStateChange
        // Это гарантирует что Navigate в auth.tsx сработает немедленно
        setUser(signedInUser);
        setSession(signedInSession);

        toast({
          title: "Вход выполнен успешно",
          description: `Добро пожаловать, ${signedInUser.email}!`,
        });

        return { data, error: null };
      }

      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    try {
      localStorage.removeItem('lastActiveRoute');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('hotelContextCache');
      
      cleanupAuthState();
      await supabase.auth.signOut({ scope: 'global' });
      
      toast({
        title: "Выход выполнен",
        description: "До свидания!",
      });
      
      window.location.href = '/auth';
    } catch {
      window.location.href = '/auth';
    }
  };

  // 🔐 Загружаем permissions при изменении пользователя
  useEffect(() => {
    if (user?.id && !permissions) {
      loadPermissions(user.id);
    } else if (!user) {
      setPermissions(null);
      setSystemRole(null);
      setPermissionsLoading(false);
    }
  }, [user?.id, loadPermissions]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    permissions,
    permissionsLoading,
    systemRole,
    hasPermission,
    refreshPermissions,
    signIn,
    signUp,
    signOut,
    forceReset,
    diagnostics,
  }), [user, session, loading, permissions, permissionsLoading, systemRole, hasPermission, refreshPermissions, diagnostics]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
